/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useState } from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Controller, useForm, FieldValues, UseFormReturn } from 'react-hook-form';
import Input from '@material-tailwind/react/Input';
import { useCookies } from 'react-cookie';
import Config from '../config/Config';
import styles from '../styles/SettingsLayout.module.css';
import { useAuth } from '../providers/Auth';
import { useApp } from '../providers/App';
import Button from '../components/Button';
import LocationField from '../components/Field/LocationField';
import Error from '../components/Field/Error';
import { inputProps, inputRules, OtherCountry } from '../const/profile';
import RegisterLayout from '../components/RegisterLayout';
import Message from '../components/Message';
import { Price } from '../interfaces/plan';
import ActiveCampaignAPIService from '../services/ActiveCampaign';
import { useFeatureHub } from '../providers/FeatureHub';

const activeCampaignService: any = ActiveCampaignAPIService();

const legalLinks = {
  terms: (
    <a className="text-red-500 underline" href={`${Config.WORDPRESS_URL}/tos/`}>
      Terms and Conditions
    </a>
  ),
  privacy: (
    <a className="text-red-500 underline" href={`${Config.WORDPRESS_URL}/privacy-policy/`}>
      Privacy Policy
    </a>
  ),
};

const Register: NextPage = () => {
  const { prices } = useApp();
  const { registerUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const form: UseFormReturn = useForm<FieldValues>({
    mode: 'all',
  });
  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isValid },
  } = form;
  const router = useRouter();
  const [cookie] = useCookies(['pendingVerification']);
  const { features } = useFeatureHub();
  let { membership } = router.query;

  const { community: communityCode } = router.query;
  const community: any = useMemo(
    () =>
      communityCode &&
      features?.communities?.find((comm) => !!comm.codePrice[communityCode as string]),
    [communityCode, features]
  );
  const resumeCommunity: any = useMemo(() => {
    let comm;
    if (cookie.pendingVerification) {
      const commName = new URLSearchParams(cookie.pendingVerification).get('community');
      if (commName) {
        comm = features?.communities?.find((_comm) => commName === _comm.name);
      }
    }
    return comm;
  }, [cookie.pendingVerification, features]);

  const getButtonIcon = () => (isValid ? 'chevron_right_white' : 'chevron_right');

  const onSubmit = async (data: any) => {
    if (communityCode && community) {
      membership = community.codePrice[communityCode as string];
    }
    const price = (membership && prices.find((_price: Price) => _price.id === membership)) as Price;

    setIsLoading(true);
    if (data.locationCountry === OtherCountry.value) {
      await activeCampaignService.updateList(data);
      router.push({
        pathname: '/unserviced',
        query: {
          mode: 'country',
          country: data.locationCustomCountry,
        },
      });
      return;
    }

    const result = await registerUser({
      ...data,
      registeredPlan: price?.id,
      communityCode: community && communityCode,
    });
    if (result.verificationId) {
      router.push({
        pathname: '/verify',
        query: {
          verificationId: result.verificationId,
          email: data.email,
          community: community?.name,
        },
      });
    } else if (result.changePasswordId) {
      router.push({
        pathname: '/password',
        query: {
          changePasswordId: result.changePasswordId,
          email: data.email,
          mode: 'setup',
        },
      });
    } else if (result.data?.data?.fieldErrors && result.data.data.fieldErrors['user.email']) {
      setIsLoading(false);
      setError('email', {
        message:
          result.data?.data.fieldErrors['user.email'][0]?.code === '[duplicate]user.email' ? (
            <>
              There is already an account with this email, try&nbsp;
              <Link href="/login" passHref>
                <a className="text-red-500 underline">logging in</a>
              </Link>
            </>
          ) : (
            result.data?.data.fieldErrors['user.email'][0]?.message
          ),
        type: 'manual',
      });
    } else {
      setIsLoading(false);
      setError('email', { message: result.message });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setValue('accept', false, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RegisterLayout pageName="Sign up to SoleSavy" community={community} showLogin>
      {!!community && (
        <div className="font-sfProTextRegular text-base text-gray-700 max-w-md">
          Join the <span className="font-sfProTextSemiBold text-gray-750">{community.name}</span>{' '}
          community and access various SoleSavy tools and perks.
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          id="register"
          className="grid grid-flow-row w-full bg-white h-auto mt-6 p-6 gap-6 rounded-lg shadow-light max-w-centerMessage"
        >
          {cookie.pendingVerification && (
            <Message>
              {resumeCommunity
                ? `Still looking to join SoleSavy through the ${resumeCommunity.name} community? `
                : 'Looks like you already have a registration session started. '}
              <Link href={`/verify?${cookie.pendingVerification}&mode=resume`}>
                Click here to resume your session
              </Link>
              .
            </Message>
          )}
          <div className={styles.formSection}>
            <div className={styles.nameSection}>
              <Controller
                control={control}
                rules={inputRules({ required: 'First name is required.' })}
                name="firstName"
                render={({ field: { value, ref, ...field } }) => (
                  <div className={styles.field}>
                    <Input
                      {...inputProps(field)}
                      defaultValue={value}
                      autoComplete="given-name"
                      placeholder="First name"
                      error={
                        errors?.firstName?.message && <Error>{errors?.firstName?.message}</Error>
                      }
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                rules={inputRules({ required: 'Last name is required.' })}
                name="lastName"
                render={({ field: { value, ref, ...field } }) => (
                  <div className={styles.field}>
                    <Input
                      {...inputProps(field)}
                      defaultValue={value}
                      autoComplete="family-name"
                      placeholder="Last name"
                      error={
                        errors?.lastName?.message && <Error>{errors?.lastName?.message}</Error>
                      }
                    />
                  </div>
                )}
              />
            </div>
            <Controller
              control={control}
              rules={inputRules({ required: 'Email address is required.', email: true })}
              name="email"
              render={({ field: { value, ref, ...field } }) => (
                <div className={styles.field}>
                  <Input
                    {...inputProps(field)}
                    type="email"
                    defaultValue={value}
                    autoComplete="email"
                    placeholder="Email address"
                    error={errors?.email?.message && <Error>{errors?.email?.message}</Error>}
                  />
                </div>
              )}
            />
            <LocationField otherCountry form={form} />
            <label htmlFor="accept" className="flex items-start gap-3 -mt-2">
              <input
                className="grow min-w-checkBox min-h-checkBox"
                {...register('accept', { required: 'Must accept' })}
                type="checkbox"
              />
              <span className="-mt-1 text-gray-600">
                I confirm I am 18 years of age or older, agree to the {legalLinks.terms} and&nbsp;
                {legalLinks.privacy}, and consent to receiving communications from SoleSavy.
              </span>
            </label>
          </div>
          <Button
            canActivate={!isLoading && isValid}
            btnContext="sign up"
            type="submit"
            rightIconName={isLoading ? 'loading' : getButtonIcon()}
            iconSize={16}
            customClasses="flex justify-between"
          />
        </div>
      </form>
    </RegisterLayout>
  );
};

export default Register;
